import { LoggerService, errorDetails } from '@agroone-front/shared'
import { Injectable, inject } from '@angular/core'
import { Action, ActionCreator, ActionReducer, ReducerTypes, Store, createReducer } from '@ngrx/store'
import * as idbKeyval from 'idb-keyval'
import { UseStore, createStore, del, entries, get, set, setMany } from 'idb-keyval'
import { INDEXDB_CONFIG, IndexDBConfig } from '../core/storage/indexdb.config'
import { clearCropDetail } from '../scene/crop-management/crop/store/actions/crop-detail.actions'
import { clearCrops } from '../scene/crop-management/crop/store/actions/crop.actions'
import { CropDetailActivities } from '../scene/crop-management/crop/store/reducers/crop-detail.reducer'
import { CropState } from '../scene/crop-management/crop/store/reducers/crop.reducer'
import { clearFarms, loadFarms } from '../scene/crop-management/farm/store/actions/farm.actions'
import { clearFields } from '../scene/crop-management/farm/store/actions/field.actions'
import { FarmState } from '../scene/crop-management/farm/store/reducers/farm.reducer'
import { FieldState } from '../scene/crop-management/farm/store/reducers/field.reducer'
import { CropService } from '../shared/crop/services/crop.service'
import { PesticideService } from '../shared/pesticide/services/pesticide.service'
import { clearLoading } from './loading/loading.actions'
import { LoadingState } from './loading/loading.reducer'

@Injectable({
  providedIn: 'root',
})
export class IdbStorageService {
  private logger = inject(LoggerService)
  private farmStore = inject<Store<FarmState>>(Store)
  private cropStore = inject<Store<CropState>>(Store)
  private cropDetailStore = inject<Store<CropDetailActivities>>(Store)
  private fieldStore = inject<Store<FieldState>>(Store)
  private loadingStore = inject<Store<LoadingState>>(Store)
  private config = inject<IndexDBConfig>(INDEXDB_CONFIG)
  private cropService = inject(CropService)
  private pesticideService = inject(PesticideService)

  public store: UseStore

  constructor() {
    this.updateStorageSize()
    this.store = createStore(`agronne-store`, 'states')
  }

  // LOCAL STORAGE
  /**
   * Saves Object in localStorage associated with key
   */
  public setLocal(key: string, value: unknown) {
    let stringValue: string
    if (typeof value !== 'string') {
      stringValue = JSON.stringify(value)
    } else {
      stringValue = value
    }
    localStorage.setItem(key, stringValue)
  }

  /**
   * Get object from localStorage with provided key
   */
  public getLocal(key: string): unknown {
    const returnValue = localStorage.getItem(key)
    if (!returnValue) {
      throw new Error(`No value in localStorage for '${key}'`)
    }
    try {
      return JSON.parse(returnValue)
    } catch (_error) {
      return returnValue
    }
  }

  /**
   * Get all objects from localStorage
   */
  public getAllLocal(): { [key: string]: unknown } {
    const m: { [key: string]: string } = { ...localStorage }
    for (const entry of Object.entries(m)) {
      let value = entry[1]
      if (value.startsWith('{') || value.startsWith('[')) {
        value = JSON.parse(value)
      }
      m[entry[0]] = value
    }
    return m
  }

  /**
   * Clears object from localStorage with provided key
   */
  public clearLocal(key: string) {
    localStorage.removeItem(key)
  }

  public async clearStore(): Promise<void> {
    await idbKeyval.clear(this.store)
  }

  /**
   * Save an object in indexedDB
   */
  public async set(key: string, value: unknown): Promise<void> {
    try {
      await set(key, value, this.store ?? undefined)
      await this.updateStorageSize()
    } catch (error) {
      if (errorDetails(error).name === 'QuotaExceededError') {
        this.logger.error(`QuotaExceededError when trying to store ${key} inside indexedDB`)
        throw error
      }
    }
  }

  /**
   * Save multiple objects in indexedDB
   */
  public async setMany(objects: [string, unknown][]): Promise<void> {
    try {
      await setMany(objects, this.store ?? undefined)
      await this.updateStorageSize()
    } catch (error) {
      if (errorDetails(error).name === 'QuotaExceededError') {
        this.logger.error('QuotaExceededError when trying to store multiple items inside indexedDB')
        throw error
      }
    }
  }

  /**
   * Get an object from indexedDB
   */
  public async get<T>(key: string): Promise<T | undefined> {
    return get<T>(key, this.store ?? undefined)
  }

  public async del(key: string): Promise<void> {
    return del(key, this.store ?? undefined)
  }

  /**
   * Get all objects in indexedDB as an array
   */
  public async getAll<T>(): Promise<{ [key: string]: T }> {
    const ks = await entries<IDBValidKey, T>(this.store ?? undefined)
    return ks.reduce<{ [key: string]: T }>((obj, entry) => {
      const key: string = entry[0].toString()
      const value: T = entry[1]
      obj[`${key}`] = value
      return obj
    }, {})
  }

  public async clear(key: string): Promise<void> {
    return del(key, this.store ?? undefined)
  }

  /**
   *
   * @param reducerKey
   * @param initialState
   * @param ons
   * @returns
   */
  public async createIdbRehydrateReducer<S, A extends Action = Action>(
    reducerKey: string,
    initialState: S,
    ...ons: ReducerTypes<S, ActionCreator[]>[]
  ): Promise<ActionReducer<S, A>> {
    const newInitialState = (await this.get<S>(reducerKey)) ?? initialState
    const newOns: ReducerTypes<S, ActionCreator[]>[] = []
    ons.forEach((oldOn: ReducerTypes<S, ActionCreator[]>) => {
      const newReducer: ReducerTypes<S, ActionCreator[]>['reducer'] = (state, action) => {
        const newState = oldOn?.reducer(state, action)
        this.set(reducerKey, newState)

        return newState
      }
      newOns.push({ ...oldOn, reducer: newReducer })
    })

    return Promise.resolve(createReducer(newInitialState, ...newOns))
  }

  public clearStorage(): void {
    this.farmStore.dispatch(clearFarms())
    this.cropStore.dispatch(clearCrops())
    this.cropDetailStore.dispatch(clearCropDetail())
    this.fieldStore.dispatch(clearFields())
    this.loadingStore.dispatch(clearLoading())
    this.pesticideService.clearCache()

    //then refresh main store
    this.farmStore.dispatch(loadFarms())
    this.cropService.dispatchLoadCrops()
  }

  private async updateStorageSize(): Promise<void> {
    if (navigator.storage && navigator.storage.estimate) {
      await navigator.storage.estimate()
    }
  }
}
